import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import {
	BarChart3,
	BookMarked,
	BookOpen,
	Brain,
	ChevronLeft,
	ChevronRight,
	Cpu,
	Database,
	LayoutDashboard,
	Library,
	Lightbulb,
	ListChecks,
	MessageCircleQuestion,
	Network,
	PenLine,
	Search,
	Settings,
	Sparkles,
	Upload,
	Video,
} from "lucide-react";

interface NavItem {
	label: string;
	path: string;
	icon: React.ReactNode;
}

interface NavSection {
	title: string;
	items: NavItem[];
}

const navSections: NavSection[] = [
	{
		title: "OVERVIEW",
		items: [{ label: "Dashboard", path: "/", icon: <LayoutDashboard size={18} /> }],
	},
	{
		title: "CURATION",
		items: [
			{ label: "Review", path: "/review", icon: <Sparkles size={18} /> },
			{ label: "Ingest", path: "/ingest", icon: <Upload size={18} /> },
			{ label: "Video Queue", path: "/video-queue", icon: <Video size={18} /> },
			{ label: "Book Queue", path: "/book-queue", icon: <BookMarked size={18} /> },
		],
	},
	{
		title: "DISCOVERY",
		items: [
			{ label: "Search", path: "/search", icon: <Search size={18} /> },
			{
				label: "Ask My Brain",
				path: "/ask",
				icon: <MessageCircleQuestion size={18} />,
			},
			{
				label: "Knowledge Graph",
				path: "/graph",
				icon: <Network size={18} />,
			},
			{
				label: "Vector KB",
				path: "/vector-kb",
				icon: <Database size={18} />,
			},
		],
	},
	{
		title: "LIBRARY",
		items: [
			{ label: "Atlas", path: "/atlas", icon: <BookOpen size={18} /> },
			{ label: "Documents", path: "/documents", icon: <Library size={18} /> },
			{ label: "Gap Queue", path: "/gap-queue", icon: <ListChecks size={18} /> },
			{ label: "Synthesis", path: "/synthesis", icon: <Lightbulb size={18} /> },
			{ label: "Creator", path: "/creator", icon: <PenLine size={18} /> },
		],
	},
	{
		title: "INSIGHTS",
		items: [
			{
				label: "Statistics",
				path: "/stats",
				icon: <BarChart3 size={18} />,
			},
		],
	},
];

interface SidebarProps {
	collapsed: boolean;
	onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;

	return (
		<aside
			className={cn(
				"relative flex flex-col h-full bg-sidebar-bg border-r border-sidebar-border transition-all duration-300 ease-out",
				collapsed ? "w-[60px]" : "w-[220px]",
			)}
		>
			{/* Logo */}
			<div className="flex items-center gap-3 px-4 h-14 shrink-0">
				<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0">
					<Brain size={18} className="text-primary" />
				</div>
				{!collapsed && (
					<div className="flex flex-col min-w-0 animate-fade-in">
						<span className="text-sm font-display font-bold text-foreground tracking-tight truncate">
							Second Brain
						</span>
					</div>
				)}
			</div>

			<Separator className="bg-sidebar-border" />

			{/* Navigation */}
			<ScrollArea className="flex-1 py-3">
				<nav className="flex flex-col gap-5 px-2">
					{navSections.map((section) => (
						<div key={section.title}>
							{!collapsed && (
								<span className="px-3 mb-1.5 text-[10px] font-semibold tracking-[0.12em] text-sidebar-fg/50 uppercase block">
									{section.title}
								</span>
							)}
							<div className="flex flex-col gap-0.5">
								{section.items.map((item) => {
									const isActive =
										item.path === "/" ? currentPath === "/" : currentPath.startsWith(item.path);

									const linkContent = (
										<Link
											key={item.path}
											to={item.path}
											className={cn(
												"group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-200",
												collapsed && "justify-center px-0 mx-1",
												isActive
													? "text-foreground bg-primary/8"
													: "text-sidebar-fg hover:text-foreground hover:bg-white/[0.03]",
											)}
										>
											{/* Active indicator — glowing red line */}
											{isActive && (
												<div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-primary shadow-[0_0_8px_rgba(212,42,42,0.5)]" />
											)}

											<span
												className={cn(
													"shrink-0 transition-colors",
													isActive ? "text-primary" : "text-sidebar-fg group-hover:text-foreground",
												)}
											>
												{item.icon}
											</span>

											{!collapsed && <span className="truncate font-medium">{item.label}</span>}
										</Link>
									);

									if (collapsed) {
										return (
											<Tooltip key={item.path} delayDuration={0}>
												<TooltipTrigger asChild>{linkContent}</TooltipTrigger>
												<TooltipContent side="right" sideOffset={8}>
													{item.label}
												</TooltipContent>
											</Tooltip>
										);
									}

									return linkContent;
								})}
							</div>
						</div>
					))}
				</nav>
			</ScrollArea>

			<Separator className="bg-sidebar-border" />

			{/* Bottom: Settings + Collapse toggle */}
			<div className="px-2 py-2 flex flex-col gap-0.5">
				{(() => {
					const isActive = currentPath === "/settings";
					const settingsLink = (
						<Link
							to="/settings"
							className={cn(
								"group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-200",
								collapsed && "justify-center px-0 mx-1",
								isActive
									? "text-foreground bg-primary/8"
									: "text-sidebar-fg hover:text-foreground hover:bg-white/[0.03]",
							)}
						>
							{isActive && (
								<div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-primary shadow-[0_0_8px_rgba(212,42,42,0.5)]" />
							)}
							<Settings
								size={18}
								className={cn(
									"shrink-0 transition-colors",
									isActive ? "text-primary" : "text-sidebar-fg group-hover:text-foreground",
								)}
							/>
							{!collapsed && <span className="truncate font-medium">Settings</span>}
						</Link>
					);

					return collapsed ? (
						<Tooltip delayDuration={0}>
							<TooltipTrigger asChild>{settingsLink}</TooltipTrigger>
							<TooltipContent side="right" sideOffset={8}>
								Settings
							</TooltipContent>
						</Tooltip>
					) : (
						settingsLink
					);
				})()}

				{(() => {
					const isActive = currentPath === "/ai-models";
					const aiModelsLink = (
						<Link
							to="/ai-models"
							className={cn(
								"group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-200",
								collapsed && "justify-center px-0 mx-1",
								isActive
									? "text-foreground bg-primary/8"
									: "text-sidebar-fg hover:text-foreground hover:bg-white/[0.03]",
							)}
						>
							{isActive && (
								<div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-primary shadow-[0_0_8px_rgba(212,42,42,0.5)]" />
							)}
							<Cpu
								size={18}
								className={cn(
									"shrink-0 transition-colors",
									isActive ? "text-primary" : "text-sidebar-fg group-hover:text-foreground",
								)}
							/>
							{!collapsed && <span className="truncate font-medium">AI Models</span>}
						</Link>
					);

					return collapsed ? (
						<Tooltip delayDuration={0}>
							<TooltipTrigger asChild>{aiModelsLink}</TooltipTrigger>
							<TooltipContent side="right" sideOffset={8}>
								AI Models
							</TooltipContent>
						</Tooltip>
					) : (
						aiModelsLink
					);
				})()}

				<button
					type="button"
					onClick={onToggle}
					className={cn(
						"flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-sidebar-fg hover:text-foreground hover:bg-white/[0.03] transition-all duration-200 cursor-pointer",
						collapsed && "justify-center px-0 mx-1",
					)}
					aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
				>
					{collapsed ? (
						<ChevronRight size={18} />
					) : (
						<>
							<ChevronLeft size={18} className="shrink-0" />
							<span className="truncate font-medium">Collapse</span>
						</>
					)}
				</button>
			</div>
		</aside>
	);
}
